import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useMercenary } from './MercenaryContext';

interface TutorialStep {
  id: number;
  name: string;
  title: string;
  description: string;
  targetElement: string | null;
  targetPage: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center';
  stepOrder: number;
  isOptional: boolean;
  prerequisiteSteps: string[] | null;
  actionType: string | null;
  actionTarget: string | null;
  completionCondition: string | null;
  isActive: boolean;
}

interface TutorialProgress {
  id: number;
  userId: number;
  mercenaryId: number | null;
  tutorialStepId: number;
  stepName: string;
  isCompleted: boolean;
  isSkipped: boolean;
  completedAt: string | null;
  skippedAt: string | null;
  attempts: number;
  TutorialStep: TutorialStep;
}

interface TutorialContextType {
  currentStep: TutorialStep | null;
  isActive: boolean;
  isLoading: boolean;
  progress: TutorialProgress[];
  startTutorial: () => void;
  completeStep: (stepName: string) => Promise<void>;
  skipStep: (stepName: string) => Promise<void>;
  nextStep: () => void;
  closeTutorial: () => void;
  resetTutorial: () => Promise<void>;
  shouldShowTutorial: boolean;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (!context) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: ReactNode;
}

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { mercenary } = useMercenary();
  const [currentStep, setCurrentStep] = useState<TutorialStep | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<TutorialProgress[]>([]);
  const [shouldShowTutorial, setShouldShowTutorial] = useState(false);

  // Check if tutorial should be shown for new mercenaries
  useEffect(() => {
    const checkTutorialStatus = async () => {
      if (!user || !mercenary) return;

      try {
        const response = await axios.get(`/api/tutorial/should-show?mercenaryId=${mercenary.id}`, {
          withCredentials: true
        });
        setShouldShowTutorial(response.data.shouldShow);
        
        if (response.data.shouldShow) {
          // Auto-start tutorial for new mercenaries
          setTimeout(() => startTutorial(), 1000);
        }
      } catch (error) {
        console.error('Error checking tutorial status:', error);
      }
    };

    checkTutorialStatus();
  }, [user, mercenary]);

  // Load tutorial progress
  useEffect(() => {
    const loadProgress = async () => {
      if (!user || !mercenary) return;

      try {
        const response = await axios.get(`/api/tutorial/progress?mercenaryId=${mercenary.id}`, {
          withCredentials: true
        });
        setProgress(response.data);
      } catch (error) {
        console.error('Error loading tutorial progress:', error);
      }
    };

    if (user && mercenary) {
      loadProgress();
    }
  }, [user, mercenary]);

  const startTutorial = async () => {
    if (!user || !mercenary) return;

    setIsLoading(true);
    try {
      const currentPage = window.location.pathname;
      const response = await axios.get(`/api/tutorial/next-step?mercenaryId=${mercenary.id}&currentPage=${currentPage}`, {
        withCredentials: true
      });

      if (response.data.id) {
        setCurrentStep(response.data);
        setIsActive(true);
      }
    } catch (error) {
      console.error('Error starting tutorial:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const completeStep = async (stepName: string) => {
    if (!user || !mercenary) return;

    try {
      await axios.post('/api/tutorial/complete-step', {
        stepName,
        mercenaryId: mercenary.id
      }, { withCredentials: true });

      // Update progress
      setProgress(prev => prev.map(p => 
        p.stepName === stepName 
          ? { ...p, isCompleted: true, completedAt: new Date().toISOString() }
          : p
      ));

      // Move to next step
      nextStep();
    } catch (error) {
      console.error('Error completing tutorial step:', error);
    }
  };

  const skipStep = async (stepName: string) => {
    if (!user || !mercenary) return;

    try {
      await axios.post('/api/tutorial/skip-step', {
        stepName,
        mercenaryId: mercenary.id
      }, { withCredentials: true });

      // Update progress
      setProgress(prev => prev.map(p => 
        p.stepName === stepName 
          ? { ...p, isSkipped: true, skippedAt: new Date().toISOString() }
          : p
      ));

      // Move to next step
      nextStep();
    } catch (error) {
      console.error('Error skipping tutorial step:', error);
    }
  };

  const nextStep = async () => {
    if (!user || !mercenary) return;

    try {
      const currentPage = window.location.pathname;
      const response = await axios.get(`/api/tutorial/next-step?mercenaryId=${mercenary.id}&currentPage=${currentPage}`, {
        withCredentials: true
      });

      if (response.data.id) {
        setCurrentStep(response.data);
      } else {
        // No more steps
        setCurrentStep(null);
        setIsActive(false);
      }
    } catch (error) {
      console.error('Error getting next tutorial step:', error);
    }
  };

  const closeTutorial = () => {
    setIsActive(false);
    setCurrentStep(null);
  };

  const resetTutorial = async () => {
    if (!user || !mercenary) return;

    try {
      await axios.post('/api/tutorial/reset', {
        mercenaryId: mercenary.id
      }, { withCredentials: true });

      setProgress([]);
      setCurrentStep(null);
      setIsActive(false);
      setShouldShowTutorial(true);
    } catch (error) {
      console.error('Error resetting tutorial:', error);
    }
  };

  const value: TutorialContextType = {
    currentStep,
    isActive,
    isLoading,
    progress,
    startTutorial,
    completeStep,
    skipStep,
    nextStep,
    closeTutorial,
    resetTutorial,
    shouldShowTutorial
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};

export default TutorialContext;
